import { CalendarDate, now, toCalendarDate } from '@internationalized/date';
import { ReactNode, useContext } from 'react';
import {
    CalendarStateContext,
    RangeCalendarStateContext,
} from 'react-aria-components';
import { CalendarState, RangeCalendarState } from 'react-stately';

import Button from '../../../Button/Button';
import HeightToggle from '../../../HeightToggle/HeightToggle';
import Row from '../../../Row/Row';
import Stack from '../../../Stack/Stack';
import useDetailLevel from '../../hooks/useDetailLevel';
import DetailLevel from '../../types/DetailLevel';
import Mode from '../../types/Mode';
import DateView from '../DateView/DateView';
import MonthView from '../MonthView/MonthView';
import YearView from '../YearView/YearView';

import isRangeCalendarContext from './helpers/isRangeCalendarContext';
import useSelectingRange from './hooks/useSelectingRange';

const valueFromState = (
    state: CalendarState | RangeCalendarState
): CalendarDate | undefined => {
    if (isRangeCalendarContext(state)) {
        if (!state.value) {
            return undefined;
        }

        return toCalendarDate(state.value.start);
    }

    return state.value ?? undefined;
};

type BaseCalendarProps = {
    visibleMonths?: number;
    mode?: Mode;

    defaultDetailLevel?: DetailLevel;
    maxDetailLevel?: DetailLevel;
    minDetailLevel?: DetailLevel;
    showDatesOutsideMonth?: boolean;
    singleDateButtonLabel?: ReactNode;
    onSelectingRangeChange?: (selectingRange: boolean) => void;
};

const BaseCalendar = ({
    visibleMonths = 1,
    mode = 'single',
    minDetailLevel = 'year',
    maxDetailLevel = 'day',
    defaultDetailLevel = maxDetailLevel,
    showDatesOutsideMonth,
    singleDateButtonLabel,
    onSelectingRangeChange,
}: BaseCalendarProps) => {
    const calendarContext = useContext(CalendarStateContext);
    const rangeCalendarContext = useContext(RangeCalendarStateContext);
    const context = mode !== 'single' ? rangeCalendarContext : calendarContext;

    if (!context) {
        throw new Error(
            'BaseCalendar must be used within a CalendarState or RangeCalendarState context.'
        );
    }

    const selectingNewRange = useSelectingRange({
        context: context,
        onChange: onSelectingRangeChange,
    });

    const { showLessDetails, showMoreDetails, currentDetailLevel } =
        useDetailLevel({
            defaultDetailLevel,
            minDetailLevel,
            maxDetailLevel,
        });

    const minDate = context.minValue
        ? toCalendarDate(context.minValue)
        : undefined;
    const maxDate = context.maxValue
        ? toCalendarDate(context.maxValue)
        : undefined;

    const value = valueFromState(context);

    const handleFocusDateChange = (newFocusDate: CalendarDate) => {
        context.setFocusedDate(newFocusDate);
        showMoreDetails?.();

        if (currentDetailLevel === maxDetailLevel) {
            context.selectDate(
                newFocusDate.set({
                    day: 1,
                    month:
                        currentDetailLevel === 'year' ? 1 : newFocusDate.month,
                })
            );
        }
    };

    const selectRangeFromAnchorDate = () => {
        if (isRangeCalendarContext(context) && context.anchorDate) {
            const anchor = context.anchorDate;
            context.setAnchorDate(null);
            context.setValue({ start: anchor, end: anchor });
        }
    };

    if (currentDetailLevel === 'year') {
        return (
            <YearView
                defaultFocusedDate={toCalendarDate(now(context.timeZone))}
                value={value}
                timeZone={context.timeZone}
                onChange={handleFocusDateChange}
                minValue={minDate}
                maxValue={maxDate}
            />
        );
    }

    if (currentDetailLevel === 'month') {
        return (
            <MonthView
                defaultFocusedDate={context.focusedDate}
                value={value}
                timeZone={context.timeZone}
                onChange={handleFocusDateChange}
                minValue={minDate}
                maxValue={maxDate}
            />
        );
    }

    return (
        <Stack>
            <DateView
                visibleRange={context.visibleRange}
                timeZone={context.timeZone}
                visibleMonths={visibleMonths}
                selectingNewRange={selectingNewRange}
                mode={mode}
                onHeadingClick={showLessDetails}
                showDatesOutsideMonth={showDatesOutsideMonth}
            />

            {mode === 'singleOrRange' && singleDateButtonLabel && (
                <Row horizontal="right">
                    <HeightToggle
                        show={selectingNewRange}
                        transition={{ delay: 0.1 }}
                    >
                        <Button
                            onClick={selectRangeFromAnchorDate}
                            size="large"
                        >
                            {singleDateButtonLabel}
                        </Button>
                    </HeightToggle>
                </Row>
            )}
        </Stack>
    );
};

export default BaseCalendar;
